<script setup lang="ts">
import SectionAccount from '@sections/section-account.vue';
import FormField from '@form/form-field.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import CoreIcon from '@core/core-icon.vue';
import CoreTimer from "@core/core-timer.vue";
import PartFooterNavigation from '@parts/part-footer-navigation.vue';

import { useUserStore } from '@store/user';
import { useUserApi } from '@store/api/userApi';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';
import { email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useToast } from "vue-toast-notification";
import { ApiError } from '@/composables/useApi';

const { t } = useI18n();
const toast = useToast();

const router = useRouter();
const userStore = useUserStore();
const userApi = useUserApi();
const { user } = storeToRefs(userStore);

const step = ref<'form' | 'confirmation'>('form');
const newEmail = ref('');
const isLoading = ref(false);
const verifyLoading = ref(false);
const verifyTimerActive = ref(false);
const verifyTimerKey = ref(0);

const hasCurrentEmail = computed(() => !!user.value?.email);
const isEmailVerified = computed(() => !!user.value?.email_verified_at);

const v$ = useVuelidate({
	email: { email },
}, { email: newEmail });

const fields = computed(() => ({
	icon: step.value === 'form' ? '/images/mail-icon.svg' : undefined,
	title: step.value === 'form' ? t('accountEmail.title') : undefined,
	loaderText: t('common.loader.email'),
}));

const saveUserData = async () => {
	try {
		if (isLoading.value) return;

		const validate = await v$.value.$validate();
		if (!validate) {
			toast.error(t('accountEmail.invalid_email'), {
				position: 'top'
			});
			return;
		}

		if (!newEmail.value || newEmail.value === user.value?.email) return;

		isLoading.value = true;
		await userStore.methods.update({ email: newEmail.value });
		step.value = 'confirmation';
	} catch (error) {
		console.error('UserUpdateError', error);
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		isLoading.value = false;
	}
};

const sendVerification = async () => {
	if (verifyLoading.value || verifyTimerActive.value) return;

	try {
		verifyLoading.value = true;
		await userApi.verifyEmail();
		verifyTimerActive.value = true;
		verifyTimerKey.value++;
	} catch (error) {
		console.error('VerifyEmailError', error);
		if (error instanceof ApiError) {
			toast.error(t(`errorCode.${error.code}`), {
				position: 'top'
			});
		}
	} finally {
		verifyLoading.value = false;
	}
};

const onVerifyTimerEnd = () => {
	verifyTimerActive.value = false;
};

const goToAccount = () => {
	router.push({ name: 'Account' });
};
</script>

<template>
	<main class="page page-email">
		<transition
			appear
			name="fade"
			style="--animation-delay: 0s;"
		>
			<section-account :fields="fields">
				<!-- Step 1: Form -->
				<template v-if="step === 'form'">
					<div class="page-email__form">
						<!-- Current email (read-only) -->
						<div v-if="hasCurrentEmail" class="page-email__section">
							<h3 class="page-email__section-title">{{ t('accountEmail.currentEmailTitle') }}</h3>
							<div class="page-email__current-card">
								<div class="page-email__current-info">
									<core-icon name="email" viewBox="0 0 18 16" class="page-email__current-icon" />
									<span class="page-email__current-text">{{ user?.email }}</span>
								</div>

								<!-- Verified -->
								<div v-if="isEmailVerified" class="page-email__verified-badge">
									<core-icon name="checkmark" viewBox="0 0 15 14" />
								</div>

								<!-- Not verified: timer or button -->
								<template v-else>
									<button
										v-if="verifyTimerActive"
										class="page-email__verify-action page-email__verify-action--timer"
										disabled
									>
										{{ t('accountEmail.verify.sent') }}
										<core-timer
											:key="verifyTimerKey"
											:duration="60"
											@end="onVerifyTimerEnd"
										/>
									</button>
									<button
										v-else
										class="page-email__verify-action"
										:disabled="verifyLoading"
										@click="sendVerification"
									>
										{{ t('accountEmail.verify.button') }}
									</button>
								</template>
							</div>
						</div>

						<!-- New email input -->
						<div class="page-email__section">
							<h3 class="page-email__section-title">{{ t('accountEmail.newEmailTitle') }}</h3>
							<form-field
								v-model="newEmail"
								:placeholder="t('accountEmail.emailPlaceholder')"
								:error="v$.email?.$errors && v$.email.$errors.length > 0"
								class="page-email__field"
							/>
						</div>
					</div>

					<part-footer-navigation
						show-back-button
						class="page-email__footer"
					>
						<button-primary
							:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
							:loading="isLoading"
							:disabled="!newEmail || newEmail === user?.email ? true : undefined"
							@click.native="saveUserData"
						>
							{{ t('common.buttons.save') }}
						</button-primary>
					</part-footer-navigation>
				</template>

				<!-- Step 2: Confirmation -->
				<template v-else>
					<div class="page-email__confirmation">
						<div class="page-email__confirmation-header">
							<img
								src="/images/mail-icon.svg"
								alt=""
								class="page-email__confirmation-icon"
							/>
							<h2 class="page-email__confirmation-title">
								{{ t('accountEmail.confirmation.title') }}
							</h2>
							<p class="page-email__confirmation-subtitle">
								{{ t('accountEmail.confirmation.subtitle') }}
								<br />
								<span class="page-email__confirmation-email">{{ newEmail }}</span>
							</p>
						</div>
						<button-primary @click="goToAccount">
							{{ t('accountEmail.confirmation.button') }}
						</button-primary>
					</div>
				</template>
			</section-account>
		</transition>
	</main>
</template>

<style lang="scss">
.page-email {
	&__form {
		display: flex;
		flex-direction: column;
		gap: 3.2rem;
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 6.4rem;
		}
	}

	&__section {
		display: flex;
		flex-direction: column;
		gap: 1.6rem;
	}

	&__section-title {
		@include font-18-pro-medium;

		@include min-desktop {
			@include font-24-pro-bold;
		}
	}

	&__current-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1.6rem;
		height: 5.2rem;
		border-radius: 1.6rem;
		background-color: var(--color-panel);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);

		@include min-desktop {
			height: 6.2rem;
		}
	}

	&__current-info {
		display: flex;
		align-items: center;
		gap: 1.6rem;
		min-width: 0;
	}

	&__current-icon {
		width: 1.8rem;
		height: 1.6rem;
		fill: var(--color-text);
		opacity: 0.5;
		flex-shrink: 0;

		@include min-desktop {
			width: 2.4rem;
			height: 2.2rem;
		}
	}

	&__current-text {
		font-size: 1.4rem;
		opacity: 0.5;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		@include min-desktop {
			@include font-18-pro-medium;
			opacity: 0.5;
		}
	}

	&__verified-badge {
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		background-color: var(--color-purple);
		fill: var(--color-white);
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: 1.2rem;
		}
	}

	&__verify-action {
		padding: 0.6rem 1.2rem;
		border-radius: 1.2rem;
		border: none;
		background-color: var(--color-purple);
		color: var(--color-white);
		font-size: 1.2rem;
		font-weight: 500;
		cursor: pointer;
		white-space: nowrap;
		transition: opacity ease 0.2s;
		flex-shrink: 0;

		@include min-desktop {
			padding: 0.8rem 1.6rem;
			font-size: 1.4rem;
		}

		&:hover:not(:disabled) {
			opacity: 0.85;
		}

		&:disabled {
			cursor: default;
		}

		&--timer {
			background-color: rgba(var(--color-active-rgb), 0.12);
			color: rgba(var(--color-text-rgb), 0.4);
			display: flex;
			align-items: center;
			gap: 0.4rem;

			.core-timer {
				display: inline;
				font-weight: 600;
			}
		}
	}

	&__confirmation {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 3.2rem;
		padding: 3.2rem 4.1rem;
		border-radius: 2.4rem;
		background-color: var(--color-panel);
	}

	&__confirmation-header {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.6rem;
		text-align: center;
	}

	&__confirmation-icon {
		width: 6.4rem;

		@include min-desktop {
			width: 6.4rem;
		}
	}

	&__confirmation-title {
		@include font-24-pro-bold;

		@include min-desktop {
			@include font-32-pro-bold;
		}
	}

	&__confirmation-subtitle {
		@include font-16-pro-regular;
		color: rgba(var(--color-text-rgb), 0.5);
		line-height: 1.2;
	}

	&__confirmation-email {
		color: var(--color-text);
	}
}
</style>
